<template>
  <ContentWrap>
    <el-form class="-mb-15px" :model="queryParams" :inline="true" label-width="80px">
      <el-form-item label="代理类型">
        <el-select v-model="queryParams.agentType" clearable placeholder="全部" class="!w-140px">
          <el-option
            v-for="o in AREA_AGENT_TYPE"
            :key="o.value"
            :label="o.label"
            :value="o.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="queryParams.status" clearable placeholder="全部" class="!w-120px">
          <el-option
            v-for="o in AREA_AGENT_TERRITORY_STATUS"
            :key="o.value"
            :label="o.label"
            :value="o.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery"><Icon icon="ep:search" class="mr-5px" />搜索</el-button>
        <el-button @click="resetQuery"><Icon icon="ep:refresh" class="mr-5px" />重置</el-button>
        <el-button
          v-hasPermi="['distribution:area-agent:territory:create']"
          type="primary"
          @click="openForm()"
        >
          <Icon icon="ep:plus" class="mr-5px" />新增费率
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-alert
      class="mb-12px"
      type="info"
      :closable="false"
      title="分红比例唯一来源：按省/市/区配置；未配置或停用视为 0%。各层拿满可叠加。"
    />
    <el-table v-loading="loading" :data="list" :stripe="true">
      <el-table-column align="center" label="编号" prop="id" width="70" />
      <el-table-column align="center" label="代理类型" width="110">
        <template #default="{ row }">
          {{ labelOf(AREA_AGENT_TYPE, row.agentType) }}
        </template>
      </el-table-column>
      <el-table-column align="center" label="行政区" min-width="140">
        <template #default="{ row }">
          {{ row.areaName || row.areaId }}
        </template>
      </el-table-column>
      <el-table-column align="center" label="分红比例%" prop="bonusRate" width="120" />
      <el-table-column align="center" label="状态" width="90">
        <template #default="{ row }">
          <el-tag :type="tagTypeOf(AREA_AGENT_TERRITORY_STATUS, row.status)" size="small">
            {{ labelOf(AREA_AGENT_TERRITORY_STATUS, row.status) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column
        align="center"
        label="创建时间"
        prop="createTime"
        width="180"
        :formatter="dateFormatter"
      />
      <el-table-column align="center" label="操作" width="160" fixed="right">
        <template #default="{ row }">
          <el-button
            v-hasPermi="['distribution:area-agent:territory:update']"
            link
            type="primary"
            @click="openForm(row)"
          >
            编辑
          </el-button>
          <el-button
            v-hasPermi="['distribution:area-agent:territory:delete']"
            link
            type="danger"
            @click="handleDelete(row)"
          >
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      :total="total"
      v-model:page="queryParams.pageNo"
      v-model:limit="queryParams.pageSize"
      @pagination="getList"
    />
  </ContentWrap>

  <Dialog v-model="dialogVisible" :title="formData.id ? '编辑辖区费率' : '新增辖区费率'" width="520px">
    <el-form ref="formRef" v-loading="formLoading" :model="formData" :rules="rules" label-width="110px">
      <el-form-item label="代理类型" prop="agentType">
        <el-select
          v-model="formData.agentType"
          class="!w-full"
          :disabled="!!formData.id"
          @change="onAgentTypeChange"
        >
          <el-option
            v-for="o in AREA_AGENT_TYPE"
            :key="o.value"
            :label="o.label"
            :value="o.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="行政区" prop="areaId">
        <el-cascader
          v-model="areaPath"
          class="!w-full"
          :options="cascaderOptions"
          :props="cascaderProps"
          clearable
          filterable
          :disabled="!formData.agentType"
          placeholder="请先选代理类型，再选对应层级行政区"
          @change="onAreaPathChange"
        />
      </el-form-item>
      <el-form-item label="分红比例%" prop="bonusRate">
        <el-input-number
          v-model="formData.bonusRate"
          :min="0"
          :max="100"
          :precision="4"
          class="!w-full"
        />
      </el-form-item>
      <el-form-item label="状态" prop="status">
        <el-radio-group v-model="formData.status">
          <el-radio
            v-for="o in AREA_AGENT_TERRITORY_STATUS"
            :key="o.value"
            :value="o.value"
          >
            {{ o.label }}
          </el-radio>
        </el-radio-group>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button :disabled="formLoading" type="primary" @click="submit">确 定</el-button>
      <el-button @click="dialogVisible = false">取 消</el-button>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import { dateFormatter } from '@/utils/formatTime'
import { getAreaTree } from '@/api/system/area'
import * as TerritoryApi from '@/api/mall/trade/areaAgent/territory'
import {
  AREA_AGENT_TYPE,
  AREA_AGENT_TERRITORY_STATUS,
  labelOf,
  tagTypeOf
} from '../constants'

defineOptions({ name: 'TradeAreaAgentTerritory' })

const message = useMessage()
const loading = ref(false)
const formLoading = ref(false)
const list = ref<TerritoryApi.AreaAgentTerritoryRateVO[]>([])
const total = ref(0)
const dialogVisible = ref(false)
const formRef = ref()
const areaTree = ref<any[]>([])
const areaPath = ref<number[]>([])

const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  agentType: undefined as number | undefined,
  status: undefined as number | undefined
})

const formData = ref<TerritoryApi.AreaAgentTerritoryRateVO>({
  agentType: 1,
  areaId: 0,
  bonusRate: 0,
  status: 0
})

const rules = reactive({
  agentType: [{ required: true, message: '请选择代理类型', trigger: 'change' }],
  areaId: [{ required: true, message: '请选择行政区', trigger: 'change' }],
  bonusRate: [{ required: true, message: '请填写比例', trigger: 'blur' }],
  status: [{ required: true, message: '请选择状态', trigger: 'change' }]
})

/** 级联：省=1层、市=2层、区=3层 */
const cascaderProps = computed(() => ({
  value: 'id',
  label: 'name',
  children: 'children',
  checkStrictly: true,
  emitPath: true
}))

/** 按代理类型裁剪树深度 */
const cascaderOptions = computed(() => {
  const depth = formData.value.agentType === 1 ? 1 : formData.value.agentType === 2 ? 2 : 3
  return clipTree(areaTree.value, depth)
})

const clipTree = (nodes: any[], depth: number, level = 1): any[] => {
  if (!nodes?.length || depth < 1) return []
  return nodes.map((n) => {
    const item: any = { id: n.id, name: n.name }
    if (level < depth && n.children?.length) {
      item.children = clipTree(n.children, depth, level + 1)
    }
    return item
  })
}

const findPath = (nodes: any[], targetId: number, path: number[] = []): number[] | null => {
  for (const n of nodes || []) {
    const next = [...path, n.id]
    if (n.id === targetId) return next
    const child = findPath(n.children || [], targetId, next)
    if (child) return child
  }
  return null
}

const getList = async () => {
  loading.value = true
  try {
    const data = await TerritoryApi.getAreaAgentTerritoryRatePage(queryParams)
    list.value = data?.list || []
    total.value = data?.total || 0
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const resetQuery = () => {
  queryParams.agentType = undefined
  queryParams.status = undefined
  handleQuery()
}

const onAgentTypeChange = () => {
  areaPath.value = []
  formData.value.areaId = 0
}

const onAreaPathChange = (val: number[] | null) => {
  if (!val?.length) {
    formData.value.areaId = 0
    return
  }
  formData.value.areaId = val[val.length - 1]
}

const openForm = (row?: TerritoryApi.AreaAgentTerritoryRateVO) => {
  if (row) {
    formData.value = { ...row }
    areaPath.value = findPath(areaTree.value, row.areaId) || [row.areaId]
  } else {
    formData.value = { agentType: 1, areaId: 0, bonusRate: 0, status: 0 }
    areaPath.value = []
  }
  dialogVisible.value = true
}

const submit = async () => {
  await formRef.value?.validate()
  if (!formData.value.areaId) {
    message.warning('请选择行政区')
    return
  }
  formLoading.value = true
  try {
    if (formData.value.id) {
      await TerritoryApi.updateAreaAgentTerritoryRate(formData.value)
    } else {
      await TerritoryApi.createAreaAgentTerritoryRate(formData.value)
    }
    message.success('保存成功')
    dialogVisible.value = false
    await getList()
  } finally {
    formLoading.value = false
  }
}

const handleDelete = async (row: TerritoryApi.AreaAgentTerritoryRateVO) => {
  try {
    await message.delConfirm()
    await TerritoryApi.deleteAreaAgentTerritoryRate(row.id!)
    message.success('已删除')
    await getList()
  } catch {}
}

onMounted(async () => {
  areaTree.value = (await getAreaTree()) || []
  await getList()
})
</script>
